import { Location } from 'history';
import { ReactElement } from 'react';
import { isUuid } from './helpers/index';
import { parse } from './queryParams';
import isNil from 'lodash/isNil';
export function getArrayValue<T = any>(value: T | T[] | undefined): T[] {
  return !value ? [] : Array.isArray(value) ? value : [value];
}
export const getArrayLastValue = (array: any) => array.length ? array[array.length - 1] : undefined;
const calculateBrightnessFromRGB = (r: number, g: number, b: number) => (r * 299 + g * 587 + b * 114) / 1000;

/**
 * Thanks to https://gist.github.com/w3core/e3d9b5b6d69a3ba8671cc84714cca8a4
 * Calculate brightness value by RGB or HEX color.
 * @param color - (String) The color value in RGB or HEX (for example: #000000 || #000 || rgb(0,0,0) || rgba(0,0,0,0))
 * @returns (Number) The brightness value (dark) 0 ... 255 (light)
 */
export const brightnessByColor = (color: string) => {
  const isHEX = color.startsWith('#');
  const isRGB = color.startsWith('rgb');
  if (isHEX) {
    const m = color.substring(1).match(color.length === 7 ? /(\S{2})/g : /(\S{1})/g);
    if (m) {
      const r = parseInt(m[0], 16);
      const g = parseInt(m[1], 16);
      const b = parseInt(m[2], 16);
      return calculateBrightnessFromRGB(r, g, b);
    }
  }
  if (isRGB) {
    const m = color.match(/(\d+){3}/g);
    if (m) {
      const r = parseInt(m[0]);
      const g = parseInt(m[1]);
      const b = parseInt(m[2]);
      return calculateBrightnessFromRGB(r, g, b);
    }
  }
  return null;
};

/**
 * Return which theme of color you should apply to an element so it contrast
 * well with the color passed in parameter.
 *
 * default: use the color that you want (default one if there is one)
 * dark: use any color that is in darker shades
 * light: use any color that is in lighter shades
 *
 * @param color - color to analyze and get what type of color you should use
 *                for a text displayed on a background color for example
 * @returns 'dark' | 'light' | 'default'
 */
export const getContrastedColorTheme = (color?: string): 'dark' | 'light' | 'default' => {
  if (!color) {
    return 'default';
  }
  // Based on https://www.w3.org/TR/AERT/#color-contrast a good contrast difference is 125
  const brightness = brightnessByColor(color);
  return brightness && brightness > 125 ? 'dark' : 'light';
};
export function extractUrlSearchParams<T extends Record<string, any>>(location?: Location): T | Record<string, unknown> {
  const urlParams = (location || window.location).search;
  const q = parse<T>(urlParams);
  return q || {};
}
export const isConsecutiveNumbers = (numbers: (number | string)[] | undefined) => {
  if (!numbers) {
    return false;
  }
  if (numbers.length <= 1) {
    return true;
  }
  const differenceArray = numbers.slice(1).map((number, index) => {
    const currentNb = typeof number === 'string' ? parseInt(number) : number;
    const nextNb = numbers[index];
    const comparedNb = typeof nextNb === 'string' ? parseInt(nextNb) : nextNb;
    return currentNb - comparedNb;
  });
  return differenceArray.every(value => value === 1);
};

/**
 * Merge react components together with custom nodes and custom separator
 * using render and renderSeparator callbacks
 * @param children - Array of react element to join
 * @param render - Callback function to display each element
 * @param renderSeparator - Callback function to display the separator between each element
 */
export function joinChildren<T>(children: T[], render: (child: T, index: number) => ReactElement | string, renderSeparator: (index: number) => ReactElement | string) {
  return children.reduce((result: ReactElement[], child: T, index: number) => {
    if (index < children.length - 1) {
      return [...result, ...[render(child, index), renderSeparator(index)]];
    }
    return [...result, render(child, index)];
  }, []);
}
export const getCommutifiErrorCode = (error: any): number | undefined => {
  if (Object.prototype.hasOwnProperty.call(error, 'error')) {
    return error.error?.data?.code ? typeof error.error?.data?.code === 'string' ? parseInt(error.error?.data.code) : error.error?.data.code : undefined;
  }
  if (Object.prototype.hasOwnProperty.call(error, 'data')) {
    return error?.data?.code ? typeof error.data?.code === 'string' ? parseInt(error.data.code) : error.data?.code : undefined;
  }
  if (Object.prototype.hasOwnProperty.call(error, 'response')) {
    return error?.response?.data?.code ? typeof error?.response?.data?.code === 'string' ? parseInt(error?.response?.data?.code) : error?.response?.data?.code : undefined;
  }
  return error.code ? parseInt(error.code) : error.code;
};
export const getHttpStatusFromError = (error: any = {}) => error && (error.response?.status || error.status || error.statusCode || error.httpCode);

/**
 * Just a tiny helper that will check if a number is defined and return
 * the value in dollars
 * @param amount - amount in cents
 * @returns amount in dollars
 */
export const centsToDollars = (amount?: number | null): number | undefined => {
  if (isNil(amount)) {
    return undefined;
  }
  return parseFloat((amount / 100).toFixed(2));
};
export const pathnameMatchesPath = (path: string, pathname: string, options?: {
  isExact?: boolean;
}): boolean => {
  if (!path || !pathname) {
    return false;
  }
  const {
    isExact
  } = {
    isExact: true,
    ...options
  };
  const pathPatternParts = path.split('/');
  const pathnameParts = pathname.split('/');
  if (isExact && pathPatternParts.length !== pathnameParts.length || pathnameParts.length < pathPatternParts.length) {
    return false;
  }
  for (let i = 0; i < pathPatternParts.length; i++) {
    const pathPart = pathPatternParts[i];
    if (pathPart.startsWith(':') && pathPart.toLowerCase().includes('id')) {
      if (!isUuid(pathnameParts[i])) {
        return false;
      }
    } else if (pathPart !== pathnameParts[i]) {
      return false;
    }
  }
  return true;
};